import React, { useState, useEffect } from "react";
import useIsomorphicLayoutEffect from "use-isomorphic-layout-effect";
import Media from "@qlik/qlik/src/components/Media";
import Cta from "@qlik/qlik/src/components/Cta";
import { useAppContext } from "@qlik/qlik/src/context/AppContext";
import MainNavConstants from "@qlik/qlik/src/lib/constants/main-nav-constants";
import { getLangPrefixedPath } from "@qlik/utils/pathPrefix";
import LevelThreeMenuMobile from "@qlik/qlik/src/components/Header/Dropdowns/Submenu_Level_Three_Mobile";
import * as styles from "./styles.module.css";
import SearchPopup from "./SearchPopup";
import Dropdowns from "./Dropdowns";
import useSiteMetadata from "@qlik/qlik/src/hooks/useSiteMetaData";
import { renderSanitizedHtmlContent, handleKeyboardEventBehavior } from "@qlik/qlik/src/lib/util/helpers";
import Link from "@qlik/qlik/src/components/Link";
import { useI18n } from "@qlik/utils/i18nContext";
import LanguageSelector from "./LanguageSelector";
import NotificationBar from "../NotificationBar";

/**
 * Search represents the search button on the header
 * @className: additional CSS classes for the button
 * @searchPopupData: sourced from content: contentfulThemeSettings.searchPopupMicrocopyGroup
 *
 * TODO: rename component to SearchButton and move to new file
 * TODO: can remove className and hardcode
 */
const Search = ({ className = "", searchPopupData }) => {
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const { setBodyClassName } = useAppContext();

    useEffect(() => {
        const bodyClassName = isSearchOpen ? "overflow-hidden" : "";
        setBodyClassName(bodyClassName);
    }, [isSearchOpen, setBodyClassName]);
    return (
        <>
            <button
                aria-label="Search"
                onClick={() => setIsSearchOpen(true)}
                className={`bg-search cursor-pointer xl:hover:opacity-80 ${className}`}
                onKeyDown={(e) => handleKeyboardEventBehavior(e, () => setIsSearchOpen(false), ["Escape"])}
                tabIndex="0"
            />
            {isSearchOpen === true ? (
                <SearchPopup setIsSearchOpen={setIsSearchOpen} searchPopupData={searchPopupData} />
            ) : (
                ""
            )}
        </>
    );
};

/**
 * Header represents the header of the website
 * Internal component structure
 * Header
 *   TopSubMenu[]: the top right corner menu
 *   Dropdowns: main menu (level 1)
 *     renderSubMenuLevelThreeComponent: left + right of each dropdown (level 2)
 *       SubmenuLevelThree<MenuName>: renders specific dropdown for each main menu item (e.g. WhyQlik)
 *         LevelThreeMenu: selects one of 10 preset layouts for the right-hand side (level 3), based on contentful configs
 *           e.g. SubmenuLayoutOne, SubmenuLayoutTwo
 *
 * @data: from contentful header entry
 * @pageSlug: - The slug of the current page
 */
const Header = ({ data, pageSlug, headerType = "Default" }) => {
    const [scrolled, setScrolled] = useState(false);
    const [scrolledDown, setScrolledDown] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const { setHeaderHeight, setSiteScrolledDown, setBodyClassName } = useAppContext();
    const [windowWidth, setWindowWidth] = useState();
    const [activeLink, setActiveLink] = useState(null);
    const [mainMenuRefs, setMainMenuRefs] = useState([]);

    const { title: defaultTitle } = useSiteMetadata();
    const { i18n } = useI18n();

    const resetSubmenus = () => {
        const secondLevelMenuesAll = document.querySelectorAll(".submenu-level-2-wrapper ul li");
        secondLevelMenuesAll.forEach((menuItem) => {
            menuItem.classList.remove("active");
        });
        const threeLevelMenuesAll = document.querySelectorAll(".submenu-level-3");
        threeLevelMenuesAll.forEach((menuItem) => {
            menuItem.classList.remove("active");
        });
        const secondLevelMenues = document.querySelectorAll(".submenu-level-2-wrapper ul");
        const secondThirdMenues = document.querySelectorAll(".submenu-level-3-wrapper");
        secondLevelMenues.forEach((menu) => menu.firstElementChild.classList.add("active"));
        secondThirdMenues.forEach((menu) => menu.firstElementChild.classList.add("active"));
    };

    const resetTopSubmenus = () => {
        const topMenusAll = document.querySelectorAll(".top-submenu");
        topMenusAll.forEach((menuItem) => {
            menuItem.classList.remove("active");
        });

        // Get the links that expands and collapses the eybrow navs
        const topMenusLinks = document.querySelectorAll("li:has(.top-submenu) > a");

        // Reset the aria-expanded value to false
        topMenusLinks.forEach((menuLinks) => {
            menuLinks.ariaExpanded = false;
        });

        // Get the Level Two links that open level three links in eyebrow nav
        const topMenuLevelThreeLinks = document.querySelectorAll(".top-submenu-level-two > a");

        // Reset the aria-expanded value to false
        topMenuLevelThreeLinks.forEach((menuLinks) => {
            menuLinks.ariaExpanded = false;
        });
    };

    // main menu click
    const handleMainMenuClick = (event) => {
        event.preventDefault();
        event.stopPropagation();
        resetTopSubmenus();
        resetSubmenus();

        if (event.target) {
            const target = event.target.closest("[data-dropdown-id]");
            const { dropdownId } = target.dataset;
            const subMenuOpen = document.querySelector(`.submenu.submenu-active[id="${dropdownId}"]`);

            // close the dropdown when clicked
            setActiveLink(subMenuOpen ? "" : dropdownId);
        }
    };

    // main menu click
    const handleMainMenuMobileClick = (event) => {
        if (event.target) {
            const target = event.target.closest("[data-main-menu-item]")?.firstElementChild;
            if (target.nextSibling) {
                target.nextSibling.classList?.add("active");
            }
        }
    };

    // mobile main menu click
    const handleMenuClick = (event) => {
        const { levelTwoParentDropdown } = event.target.closest("[data-level-two-parent-dropdown]")?.dataset;
        if (event.target) {
            const childDropdownId = document.querySelector(
                `.mobileSubmenu[data-level-two-child-dropdown="${levelTwoParentDropdown}"]`
            );
            childDropdownId?.classList.add("active");
        }
    };

    /* back button click */
    const subMenuOnClick = (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (event.target) {
            const { childDropdownId } = event.target.closest("[data-child-dropdown-id]")?.dataset;
            const parentDropdownId = document.querySelector(
                `.mobileSubmenu[data-level-two-child-dropdown="${childDropdownId}"]`
            );
            parentDropdownId?.classList.remove("active");
        }
    };

    const subMenuMainOnClick = (event) => {
        if (event.target) {
            const { childDropdownId } = event.target.closest("[data-child-dropdown-id]")?.dataset;
            const parentDropdownId = document.querySelector(
                `.mobileSubmenu[data-parent-dropdown-id="${childDropdownId}"]`
            );
            parentDropdownId?.classList.remove("active");
        }
    };

    const topSubMenuLevelOneBack = (event) => {
        if (event.target) {
            const { childDropdownId } = event.target.closest("[data-child-dropdown-id]")?.dataset;
            const parentDropdownId = document.querySelector(
                `.top-submenu[data-parent-dropdown-id="${childDropdownId}"]`
            );
            parentDropdownId?.classList.remove("active");
        }
    };

    const clickBackOnTopSubMenuLevelTwo = (event) => {
        if (event.target) {
            const { childDropdownId } = event.target.closest("[data-child-dropdown-id]")?.dataset;
            const parentDropdownId = document.querySelector(
                `.top-submenu-level-three[data-parent-dropdown-id="${childDropdownId}"]`
            );
            parentDropdownId?.classList.remove("active");
        }
    };

    /**
     * Function to toggle the menu.
     */
    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    useEffect(() => {
        const bodyClassName = isMenuOpen ? "overflow-hidden" : "";
        setBodyClassName(bodyClassName);
    }, [isMenuOpen, setBodyClassName]);

    // Scrolling direction, header sizing context updating
    // Approach re: https://stackoverflow.com/questions/62497110/detect-scroll-direction-in-react-js/62497293#62497293

    const [scrollDir, setScrollDir] = useState("scrolling down");

    useIsomorphicLayoutEffect(() => {
        // Threshold, optional
        const threshold = 0;
        let lastScrollY = window.scrollY;
        let ticking = false;

        // Funciton to identify which direction the user is scrolling
        const updateScrollDir = () => {
            const scrollY = window.scrollY;

            if (Math.abs(scrollY - lastScrollY) < threshold) {
                ticking = false;
                return;
            }

            setScrollDir(scrollY > lastScrollY ? "down" : "up");
            lastScrollY = scrollY > 0 ? scrollY : 0;
            ticking = false;
        };

        // Function to be called when user scrolls
        const onScroll = () => {
            if (!ticking) {
                window.requestAnimationFrame(updateScrollDir);
                ticking = true;
            }

            // If user has scrolled away from the top of the page
            if (window.scrollY > 0) {
                // Set the scrolled state, and (context) setSiteScrolledDown state to true
                setScrolled(true);
            } else {
                // Set the scrolled state, and (context) setSiteScrolledDown state to false
                setScrolled(false);
            }

            if (scrollDir === "down") {
                // If the user is scrolling down
                setScrolledDown(true); // Update state
                setSiteScrolledDown(true); // Update context state
                // Set the header height to the small version
                window.innerWidth > 1200 ?? setHeaderHeight(MainNavConstants.NAV_HEIGHT_SM);
            } else if (scrollDir === "up") {
                setScrolledDown(false); // Update state
                setSiteScrolledDown(false); // Update context state
                // Set the header height to the small version
                window.innerWidth > 1200 ?? setHeaderHeight(MainNavConstants.NAV_HEIGHT_LG);
            }
        };

        // Kick off the event listener
        window.addEventListener("scroll", onScroll);

        // Cleanup
        return () => window.removeEventListener("scroll", onScroll);
    }, [scrollDir, setHeaderHeight, setSiteScrolledDown]);

    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };

        handleResize();
        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const mobileThreshold = 1199; // Adjust as needed
    const isMobile = windowWidth <= mobileThreshold;

    // Handles clicks on top level links in eyebrow menu
    const topSubMenuLevelOneClick = (event) => {
        event.preventDefault();
        const target = event.target.closest("[data-level-two-parent-dropdown]");
        const { levelTwoParentDropdown } = target.closest("[data-level-two-parent-dropdown]")?.dataset;

        // Reset: Set all eyebrow nav links to aria-expanded: false first
        const eyebrowNavLinks = document.querySelectorAll(`.top-menu > li > a`);
        eyebrowNavLinks.forEach((navLink) => (navLink.ariaExpanded = false));

        if (target) {
            // On opening the eyebrow nav, set aria-expanded to true
            target.ariaExpanded = true;
            const childDropdownId = document.querySelector(
                `.top-submenu[data-parent-dropdown-id="${levelTwoParentDropdown}"]`
            );

            const topSubmenuElms = document.querySelectorAll(".top-submenu");
            const otherChildDropdowns = Array.from(topSubmenuElms).filter(
                (menuItem) => menuItem.dataset.parentDropdownId !== levelTwoParentDropdown
            );

            if (!isMobile) {
                otherChildDropdowns.forEach((menuItem) => {
                    menuItem.classList.remove("active");
                });
            }

            if (childDropdownId) {
                childDropdownId.classList.toggle("active");

                // Just check if this toggles the "active" class off, then set the aria-expanded attr to false too
                if (!childDropdownId.classList.contains("active")) {
                    target.ariaExpanded = false;
                }
            }

            // Configure the GA4 tracking event
            const clickEvent = new CustomEvent("ga-event", {
                detail: { navClickLevel: "eyebrow", primaryNavName: target.innerText },
            });

            // Dispatch the GA4 tracking event
            window.dispatchEvent(clickEvent);
        }
    };

    // Handles clicks on 2nd level links in the eyebrow menu,
    // except for the "Login" submenu, which has a 3rd level.
    const topSubMenuLevelTwoClick = (event) => {
        const topSubMenuTitle = event.target
            .closest("div.top-submenu")
            .closest("li")
            .querySelector("a").innerText;

        // Configure the GA4 tracking event
        const clickEvent = new CustomEvent("ga-event", {
            detail: {
                navClickLevel: "eyebrow",
                primaryNavName: topSubMenuTitle,
                secondaryNavName: event.target.innerText,
            },
        });

        // Dispatch the GA4 tracking event
        window.dispatchEvent(clickEvent);
    };

    // Handles clicks on 2nd level links in the eyebrow menu,
    // specifically the 2nd level links under the "Login" 3rd level menu item.
    // These links currently are Login -> Qlik / Talend
    const topSubMenuLevelTwoClickAlternate = (event) => {
        const target = event.target.closest("[data-level-three-parent-dropdown]");
        const { levelThreeParentDropdown } = target?.dataset;
        if (target) {
            // On click, set aria-expanded to true
            target.ariaExpanded = true;

            const childDropdownId = document.querySelector(
                `.top-submenu-level-three[data-parent-dropdown-id="${levelThreeParentDropdown}"]`
            );
            childDropdownId?.classList.add("active");

            // Get the label/text of the 1st level eyebrow menu item
            const primaryNavName = target.closest("li.menuItem").querySelector("a").innerText || "";

            // Get the label/text of this menu item
            const secondaryNavName = target.innerText || "";

            // Configure the GA4 tracking event
            const clickEvent = new CustomEvent("ga-event", {
                detail: {
                    navClickLevel: "eyebrow",
                    primaryNavName: primaryNavName,
                    secondaryNavName: secondaryNavName,
                },
            });

            // Dispatch the GA4 tracking event
            window.dispatchEvent(clickEvent);
        }
    };

    // Handles click on 3rd level eyebrow nav links
    // These are currently the 3rd level ones under Login -> (Qlik / Talend)
    const topSubMenuLevelThreeClick = (event) => {
        if (event.target) {
            // Get the label/text of the 1st level eyebrow menu item
            const primaryNavName = event.target.closest("li.menuItem").querySelector("a").innerText || "";

            // Get the label/text of the 2nd level (this link's parent) eyebrow menu item
            const secondaryNavName =
                event.target.closest("li.top-submenu-level-two").querySelector("a").innerText || "";

            // Get the label/text of this menu item
            const tertiaryNavName = event.target.innerText || "";

            // Configure the GA4 tracking event
            const clickEvent = new CustomEvent("ga-event", {
                detail: {
                    navClickLevel: "eyebrow",
                    primaryNavName: primaryNavName,
                    secondaryNavName: secondaryNavName,
                    tertiaryNavName: tertiaryNavName,
                },
            });

            // Dispatch the GA4 tracking event
            window.dispatchEvent(clickEvent);
        }
    };

    // Add the scrolled ("scroll-down") class to the header if the page has been scrolled
    const headerClasses = `header relative z-2 ${activeLink ? "submenu-open" : ""} ${scrolled ? "scrolled" : ""} ${
        scrolledDown ? "scroll-down" : ""
    }`;

    // Add the open class to the menu if the menu is open
    const toggleMenuClass = `${isMenuOpen ? "open" : ""}`;

    // Deconstruct data from props
    const { siteLogos, topMenu, mainMenu, cta, dynamicHeaderCta } = data;

    // Find the Cta data for the current page
    const currentCtaData = dynamicHeaderCta
        ? dynamicHeaderCta.find((button) => button.page && button.page.slug === pageSlug)
        : null;

    // If currentButtonData exists, update ctaButtons with the buttons from currentButtonData
    let ctaButtons = cta;
    if (currentCtaData) ctaButtons = [currentCtaData.tryForFreeCta, currentCtaData.requestADemoCta].filter(Boolean);

    // If "Contact CTA only" header type, only show the *second* CTA in the list
    if (headerType === "Contact CTA only") ctaButtons = ctaButtons?.slice(1);

    // main menu reference create for mouse enter events
    useEffect(() => {
        setMainMenuRefs((refs) =>
            Array(mainMenu?.menuItems?.length)
                .fill()
                .map((_, i) => refs[i] || React.createRef())
        );
    }, [mainMenu?.menuItems?.length]);

    // main menu mouse enter event handler
    useEffect(() => {
        mainMenuRefs.forEach((menuRef) => {
            if (menuRef.current) {
                menuRef.current.setAttribute("data-main-menu-item", true);
                if (isMobile) {
                    menuRef.current.addEventListener("click", handleMainMenuMobileClick);
                } else {
                    menuRef.current.addEventListener("click", handleMainMenuClick);
                }
            }
        });
        return () => {
            mainMenuRefs.forEach((menuRef) => {
                if (menuRef.current) {
                    menuRef.current.setAttribute("data-main-menu-item", true);
                    if (isMobile) {
                        menuRef.current.removeEventListener("click", handleMainMenuMobileClick);
                    } else {
                        menuRef.current.removeEventListener("click", handleMainMenuClick);
                    }
                }
            });
        };
    }, [mainMenuRefs, windowWidth]);

    // close top-menu on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            const topMenuElement = document.querySelector(".top-menu");
            if (topMenuElement && !topMenuElement.contains(event.target)) {
                resetTopSubmenus();
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    /**
     * TopSubMenu represents a menu item on the top right of the header
     * @topSubMenu: data from contentful header entry
     *
     * TODO: rename to TopMenu and move to separate file
     *
     */
    let TopSubMenu = ({ topSubMenu }) => {
        let topMenuHasThirdLevel = topSubMenu.filter((menuItem) => menuItem?.subItems?.length > 0);
        let isTopMenuHasThirdLevel = topMenuHasThirdLevel.length > 0;

        // TODO: consider replacing the terneray operator block with TopMenuTwoLevel and TopMenuThreeLevel
        return isTopMenuHasThirdLevel ? (
            <ul className="subMenuType2 max-xl:pt-5 flex flex-col xl:gap-2.5 xl:shadow-xlSmall">
                {topSubMenu?.map((topLevelTwoMenuItem, index) => {
                    const {
                        id: tL2Id,
                        title: tL2Title,
                        pageLink: tL2PageLink,
                        externalUrl: tL2ExternalUrl,
                    } = topLevelTwoMenuItem;
                    const activeTopLevelTwoClass = index === 0 ? "active" : "";
                    return (
                        <li
                            data-level-three-parent-dropdown={tL2Id}
                            key={tL2Id}
                            onMouseEnter={(event) => {
                                const { levelThreeParentDropdown } = event.target.dataset;
                                if (event.target.localName === "a") {
                                    const allTopSubmenuLevelTwoElm = document.querySelectorAll(
                                        ".top-submenu-level-two"
                                    );
                                    allTopSubmenuLevelTwoElm.forEach((menuItem) => {
                                        menuItem.classList.remove("active");
                                    });
                                    const childDropdownId = document.querySelector(
                                        `.top-submenu-level-two[data-level-three-parent-dropdown="${levelThreeParentDropdown}"]`
                                    );
                                    childDropdownId?.classList.add("active");

                                    // Set aria-expanded to false for all seubmenu level two links (reset)
                                    const childDropdownLinks = document.querySelectorAll(`.top-submenu-level-two > a`);
                                    childDropdownLinks.forEach((menuLinks) => {
                                        menuLinks.ariaExpanded = false;
                                    });

                                    // Set aria-expanded to true for the active link
                                    const childDropdownHoveredLink = document.querySelector(
                                        `.top-submenu-level-two[data-level-three-parent-dropdown="${levelThreeParentDropdown}"] > a`
                                    );
                                    childDropdownHoveredLink.ariaExpanded = true;
                                }
                            }}
                            className={`top-submenu-level-two group/hasSubMenu cursor-pointer max-xl:px-5 max-xl:py-3.75 ${activeTopLevelTwoClass}`}
                        >
                            <Link
                                data-level-three-parent-dropdown={tL2Id}
                                onClick={topSubMenuLevelTwoClickAlternate}
                                to={tL2PageLink ? i18n.getLangPrefixedPath(`/${tL2PageLink.slug}`) : tL2ExternalUrl}
                                className="z-1 group/link relative flex flex-col justify-center gap-1.5 h-full pl-5.5 text-gray tracking-normal leading-tight transition-colors duration-300
                                                                                            xl:px-5 xl:py-2.5 xl:pl-5 xl:text-base xl:font-semibold
                    before:content-[''] before:absolute before:h-full before:w-0.75 before:right-0 before:-t-0.25 before:bg-green before:opacity-0 before:transition-opacity before:duration-300 xl:group-hover/hasSubMenu:before:opacity-100 xl:group-hover/hasSubMenu:text-green xl:group-hover/hasSubMenu:bg-white
                    max-xl:text-md max-xl:pl-0 max-xl:after:content-empty max-xl:after:!block max-xl:after:absolute max-xl:after:arrow-grey-light max-xl:after:top-1.5 max-xl:after:right-0"
                                rel="noreferrer"
                                aria-expanded={index === 0}
                                role="button"
                                tabIndex={0}
                            >
                                {tL2Title}
                            </Link>
                            <div
                                className="top-submenu-l3-mobile top-submenu-level-three"
                                data-parent-dropdown-id={tL2Id}
                            >
                                <div
                                    role="button"
                                    onClick={clickBackOnTopSubMenuLevelTwo}
                                    tabIndex={-1}
                                    onKeyDown={(e) => {
                                        handleKeyboardEventBehavior(
                                            e,
                                            () => {
                                                clickBackOnTopSubMenuLevelTwo(e);
                                            },
                                            ["Escape"]
                                        );
                                    }}
                                    data-child-dropdown-id={tL2Id}
                                    className="cursor-pointer flex items-center gap-3.75 px-5 py-3.75 bg-stone-light text-sm font-medium xl:hidden"
                                >
                                    <span className="arrow-grey rotate-180"></span>
                                    Back
                                </div>
                                <ul
                                    data-parent-dropdown-id={tL2Id}
                                    className="flex flex-col pl-5 xl:top-0 xl:left-0 xl:w-[255px] pt-6 xl:h-full xl:pb-4.25 xl:px-7.5 xl:hidden xl:absolute xl:group-hover/hasSubMenu:flex gap-3.75 xl:rounded-l xl:bg-white"
                                >
                                    {topLevelTwoMenuItem?.subItems?.map((topLevelThreeMenuItem) => {
                                        const {
                                            id: tL3Id,
                                            title: tL3Title,
                                            pageLink: tL3PageLink,
                                            externalUrl: tL3ExternalUrl,
                                        } = topLevelThreeMenuItem;
                                        return (
                                            <li key={tL3Id}>
                                                <Link
                                                    onClick={topSubMenuLevelThreeClick}
                                                    to={
                                                        tL3PageLink
                                                            ? i18n.getLangPrefixedPath(`/${tL3PageLink.slug}`)
                                                            : tL3ExternalUrl
                                                    }
                                                    className="z-1 group/link relative flex flex-col justify-center gap-1.5 h-full pl-5.5 text-gray tracking-normal leading-tight xl:text-sm transition-colors duration-300 xl:pl-4.25
                    before:content-[''] before:absolute before:h-full before:w-0.5 before:left-0 before:t-0 before:bg-stone/50 before:transition-colors before:duration-300 xl:hover:before:bg-green xl:hover:text-green"
                                                >
                                                    <span className="-z-1 absolute -inset-2.5 rounded bg-white shadow-xlSmall opacity-0 transition-opacity transition-300 xl:group-hover/link:opacity-100" />
                                                    {tL3Title}
                                                </Link>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        </li>
                    );
                })}
            </ul>
        ) : (
            <ul className="subMenuType1 max-xl:p-5 flex flex-col max-xl:gap-6 xl:gap-3.75 xl:shadow-xlSmall">
                {topSubMenu?.map((topLevelTwoMenuItem) => {
                    const {
                        id: tL2Id,
                        title: tL2Title,
                        pageLink: tL2PageLink,
                        externalUrl: tL2ExternalUrl,
                    } = topLevelTwoMenuItem;
                    return (
                        <li key={tL2Id}>
                            <Link
                                onClick={topSubMenuLevelTwoClick}
                                to={tL2PageLink ? i18n.getLangPrefixedPath(`/${tL2PageLink.slug}`) : tL2ExternalUrl}
                                className="z-1 group/link relative flex flex-col justify-center gap-1.5 h-full pl-5.5 text-gray tracking-normal leading-tight xl:text-sm transition-colors duration-300 xl:pl-4.25
                    before:content-[''] before:absolute before:h-full before:w-0.5 before:left-0 before:t-0 before:bg-stone/50 before:transition-colors before:duration-300 xl:hover:before:bg-green xl:hover:text-green"
                            >
                                <span className="-z-1 absolute -inset-2.5 rounded bg-white shadow-xlSmall opacity-0 transition-opacity transition-300 xl:group-hover/link:opacity-100" />
                                {tL2Title}
                            </Link>
                        </li>
                    );
                })}
            </ul>
        );
    };

    // Extracts paths from notificationbar into pageList const and split into an array called pageListArray
    const pageList = data?.notificationBar?.paths?.paths;
    const pageListArray = pageList ? pageList.split("\n") : [];

    // Check if the current page is in the array (or if array is empty)
    const enabledOnPage = pageListArray.length === 0 || pageListArray.indexOf(pageSlug) > -1;

    // Crete a boolean based on if the pageListArray is empty or the current page is not in the pageListArray
    const isPrimaryNotificationDisable = pageListArray.length === 0 ? true : !enabledOnPage;

    // Extracts paths from secondary notificationbar into secondaryPageList const and split into an array called secondaryPageListArray
    const secondaryPageList = data.secondaryNotificationBar?.paths?.paths;
    const secondaryPageListArray = secondaryPageList ? secondaryPageList.split("\n") : [];

    // Check if the current page is in the array (or if array is empty)
    const secondaryEnabledOnPage = secondaryPageListArray.length === 0 || secondaryPageListArray.indexOf(pageSlug) > -1;

    // Group of Notification bars
    const NotificationBarGroup = () => {
        return (
            <>
                {enabledOnPage && data.showNotifications && <NotificationBar data={data.notificationBar} />}
                {isPrimaryNotificationDisable && secondaryEnabledOnPage && data.showNotifications && (
                    <NotificationBar data={data.secondaryNotificationBar} isSecondaryNotification={true} />
                )}
            </>
        );
    };

    return headerType === "Contact CTA only" || headerType === "Top CTAs only" || headerType === "Logo only" ? (
        <header className="z-50 sticky top-0 inset-x-0">
            <NotificationBarGroup />
            <div className={`${headerClasses} ${toggleMenuClass} ${styles.header} bg-white`}>
                <div className="max-xl:container-fluid xl:container">
                    <div className="row">
                        <div className="col-12">
                            <div className="headerInner flex justify-between py-3.75 max-xl:px-1.25">
                                <div className="h-7.5 xl:self-center xl:flex-[0_0_72px]">
                                    <Link to={i18n.getLangPrefixedPath("/")}>
                                        {siteLogos?.[0]?.secure_url && (
                                            <Media
                                                link={siteLogos?.[0]?.secure_url}
                                                className="logo w-auto h-full"
                                                alt={defaultTitle}
                                            />
                                        )}
                                    </Link>
                                </div>

                                {headerType !== "Logo only" && (
                                    <>
                                        <div
                                            className="headerNavigation pl-2.5 max-xl:fixed max-xl:overflow-auto max-xl:flex max-xl:flex-col max-xl:top-15 max-xl:left-0 max-xl:right-0 max-xl:px-5 max-xl:pt-10 max-xl:bg-white max-xl:-translate-x-full
              max-xl:transition-transform max-xl:duration-300 max-xl:ease-linear"
                                        >
                                            <ul className="flex gap-1.25 flex-wrap xl:gap-2.5">
                                                {ctaButtons?.map((button) => (
                                                    <Cta
                                                        key={button.id}
                                                        data={button}
                                                        className="py-3 px-4.5 text-sm max-xl:px-7.25 max-xl:py-3.25"
                                                    />
                                                ))}
                                            </ul>
                                        </div>
                                        <button
                                            className="menuToggle max-xl:w-7.5  max-xl:h-7.5  max-xl:bg-hamburger  max-xl:bg-no-repeat max-xl:bg-center xl:hidden"
                                            onClick={toggleMenu}
                                        />
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </header>
    ) : (
        <header className="z-50 sticky top-0 inset-x-0">
            <NotificationBarGroup />
            <div className={`${headerClasses} ${toggleMenuClass} ${styles.header} bg-white`}>
                <div className="max-xl:container-fluid xl:container">
                    <div className="row">
                        <div className="col-12">
                            <div
                                className={`headerInner ${styles.headerInner} flex max-xl:justify-between max-xl:py-3.75 max-xl:px-1.25`}
                            >
                                <div className="headerLogo h-7.5 xl:self-end xl:flex-[0_0_72px] xl:mb-6.25">
                                    <Link to={i18n.getLangPrefixedPath("/")}>
                                        {siteLogos?.[0]?.secure_url && (
                                            <Media
                                                link={siteLogos?.[0]?.secure_url}
                                                className="logo w-auto h-full"
                                                alt={defaultTitle}
                                            />
                                        )}
                                    </Link>
                                </div>
                                <div
                                    className={`headerNavigation ${styles.headerNavigation}
              max-xl:fixed max-xl:overflow-auto max-xl:flex max-xl:flex-col max-xl:top-15 max-xl:left-0 max-xl:right-0 max-xl:px-5 max-xl:bg-white max-xl:-translate-x-full
              max-xl:transition-transform max-xl:duration-300 max-xl:ease-linear
              xl:grid xl:w-full`}
                                >
                                    <div
                                        className={`headerNavigationTop ${styles.headerNavigationTop} max-xl:order-2
                xl:max-h-20 xl:pt-5.25 xl:opacity-100 xl:transition-all xl:duration-300 xl:ease-in-out`}
                                    >
                                        <ul
                                            className="top-menu flex max-xl:flex-col max-xl:gap-5 max-xl:py-10.25 max-xl:border-t max-xl:border-gray/30
                  xl:justify-end xl:gap-x-7.75 xl:w-fit xl:mr-0 xl:ml-auto"
                                        >
                                            {topMenu?.menuItems?.map((menuItem) => {
                                                return (
                                                    menuItem?.id &&
                                                    menuItem?.externalUrl && (
                                                        <li
                                                            data-level-two-parent-dropdown={menuItem?.id}
                                                            className="group/topMenu menuItem relative
                                                        xl:after:content-[''] xl:after:absolute xl:after:w-full xl:after:h-10 xl:after:hidden xl:hover:after:block"
                                                            key={`${menuItem.id}`}
                                                        >
                                                            <Link
                                                                onClick={topSubMenuLevelOneClick}
                                                                aria-expanded={false}
                                                                to={menuItem?.externalUrl}
                                                                data-level-two-parent-dropdown={menuItem?.id}
                                                                className="cursor-pointer group/menuLink relative flex items-center text-gray opacity-75
          max-xl:text-base max-xl:leading-snug
          max-xl:after:content-empty max-xl:after:absolute max-xl:after:arrow-grey-light max-xl:after:right-0
         xl:min-h-4 xl:pl-6 xl:text-xs xl:font-medium xl:leading-none xl:tracking-wide1 xl:uppercase xl:transition-colors xl:ease-in-out xl:duration-300
          xl:group-hover/topMenu:text-green xl:group-hover/topMenu:opacity-100"
                                                            >
                                                                {menuItem?.icon?.[0]?.secure_url && (
                                                                    <Media
                                                                        link={menuItem?.icon?.[0]?.secure_url}
                                                                        alt={
                                                                            menuItem?.icon?.[0]?.context?.custom.alt ||
                                                                            menuItem?.title
                                                                        }
                                                                        className="max-xl:w-4.25 max-xl:h-4.25 max-xl:mr-3.5 max-xl:object-contain xl:group-hover/topMenu:filter-green xl:-z-1 xl:absolute xl:-top-0 xl:left-0  xl:h-4 xl:w-4"
                                                                    />
                                                                )}
                                                                {menuItem?.title}
                                                            </Link>
                                                            {menuItem?.subItems && (
                                                                <div
                                                                    className="top-submenu-l2-mobile top-submenu"
                                                                    data-parent-dropdown-id={menuItem.id}
                                                                >
                                                                    <div
                                                                        role="button"
                                                                        onClick={topSubMenuLevelOneBack}
                                                                        tabIndex={-1}
                                                                        onKeyDown={(e) => {
                                                                            handleKeyboardEventBehavior(
                                                                                e,
                                                                                () => {
                                                                                    topSubMenuLevelOneBack(e);
                                                                                },
                                                                                ["Escape"]
                                                                            );
                                                                        }}
                                                                        data-child-dropdown-id={menuItem.id}
                                                                        className="cursor-pointer flex items-center gap-3.75 px-5 py-3.75 bg-stone-light text-sm font-medium xl:hidden"
                                                                    >
                                                                        <span className="arrow-grey rotate-180"></span>
                                                                        Back
                                                                    </div>

                                                                    <TopSubMenu
                                                                        topSubMenu={menuItem?.subItems}
                                                                        menuId={menuItem.id}
                                                                    />
                                                                </div>
                                                            )}
                                                        </li>
                                                    )
                                                );
                                            })}
                                        </ul>
                                    </div>
                                    <div
                                        className={`${styles.headerNavigationBottomLeft} headerNavigationBottomLeft relative max-xl:order-1 xl:relative xl:flex xl:pl-15
                xl:before:w-px xl:before:h-10 xl:before:bg-stone/50 xl:before:absolute xl:before:left-10.25`}
                                    >
                                        <ul className="main-menu flex max-xl:flex-col max-xl:gap-5 max-xl:py-10.25 max-xl:border-t max-xl:border-gray/30">
                                            {mainMenu?.menuItems?.map(
                                                (
                                                    menuItem,
                                                    index // TODO: refactor this block (level 1 menu) into a component for more clarity
                                                ) =>
                                                    menuItem?.id &&
                                                    menuItem?.title && (
                                                        <li
                                                            ref={mainMenuRefs[index]}
                                                            key={menuItem.id}
                                                            className="menuItem group xl:mt-[33px] relative"
                                                        >
                                                            <Link
                                                                data-dropdown-id={menuItem.id}
                                                                to={
                                                                    menuItem?.subItems === null
                                                                        ? menuItem?.pageLink
                                                                            ? i18n.getLangPrefixedPath(
                                                                                  `/${menuItem?.pageLink?.slug}`
                                                                              )
                                                                            : menuItem?.externalUrl
                                                                        : null
                                                                }
                                                                aria-expanded={false}
                                                                role={menuItem?.subItems === null ? "link" : "button"}
                                                                className={`cursor-pointer menuLink group inline-block align-top text-gray
                            max-xl:relative max-xl:block max-xl:w-full max-xl:text-md max-xl:leading-relaxed
                            max-xl:after:content-empty max-xl:after:!block max-xl:after:absolute max-xl:after:arrow-grey-light max-xl:after:right-0
                            xl:cursor-pointer xl:px-5 xl:pb-8 xl:text-base xl:font-semibold xl:leading-none xl:tracking-tight1 xl:transition-colors xl:ease-in-out xl:duration-300 xl:hover:text-green ${menuItem.id ===
                                activeLink && "active"} `}
                                                            >
                                                                {menuItem.title}
                                                            </Link>
                                                            {menuItem?.subItems && ( // TODO: consider refactor as MainMenuMobile
                                                                <div
                                                                    data-parent-dropdown-id={menuItem.id}
                                                                    className={`mobileSubmenu mobileSubmenuLevelTwo ${styles.mobileSubmenu}`}
                                                                >
                                                                    <div
                                                                        role="button"
                                                                        onClick={subMenuMainOnClick}
                                                                        tabIndex={-1}
                                                                        onKeyDown={(e) => {
                                                                            handleKeyboardEventBehavior(
                                                                                e,
                                                                                () => {
                                                                                    subMenuMainOnClick(e);
                                                                                },
                                                                                ["Escape"]
                                                                            );
                                                                        }}
                                                                        data-child-dropdown-id={menuItem.id}
                                                                        className="cursor-pointer flex items-center gap-3.75 px-5 py-3.75 bg-stone-light text-sm font-medium"
                                                                    >
                                                                        <span className="arrow-grey rotate-180"></span>
                                                                        Back
                                                                    </div>
                                                                    <ul className="pt-6.25">
                                                                        {/* 2nd level Submenu */}
                                                                        {menuItem?.subItems?.map(
                                                                            (levelTwoMenuItem, l2Index) => {
                                                                                const {
                                                                                    id: l2Id,
                                                                                    title: l2Title,
                                                                                    itemName: l2ItemName,
                                                                                    pageLink: l2PageLink,
                                                                                    externalUrl: l2ExternalUrl,
                                                                                    description: l2Description,
                                                                                } = levelTwoMenuItem;
                                                                                return (
                                                                                    <li
                                                                                        key={l2Id}
                                                                                        role="button"
                                                                                        onClick={handleMenuClick}
                                                                                        tabIndex={-1}
                                                                                        onKeyDown={(e) => {
                                                                                            handleKeyboardEventBehavior(
                                                                                                e,
                                                                                                () => {
                                                                                                    handleMenuClick(e);
                                                                                                },
                                                                                                ["Enter"]
                                                                                            );
                                                                                        }}
                                                                                        className="px-5 py-3.75"
                                                                                        data-level-two-parent-dropdown={
                                                                                            l2Id
                                                                                        }
                                                                                    >
                                                                                        <Link
                                                                                            className="relative flex flex-col xl:py-5"
                                                                                            to={
                                                                                                levelTwoMenuItem?.subItems ===
                                                                                                null
                                                                                                    ? l2PageLink
                                                                                                        ? i18n.getLangPrefixedPath(
                                                                                                              `/${l2PageLink.slug}`
                                                                                                          )
                                                                                                        : l2ExternalUrl
                                                                                                    : null
                                                                                            }
                                                                                            role={
                                                                                                levelTwoMenuItem?.subItems ===
                                                                                                null
                                                                                                    ? "link"
                                                                                                    : "button"
                                                                                            }
                                                                                        >
                                                                                            {l2Title && (
                                                                                                <span
                                                                                                    className="cursor-pointer relative block text-md leading-relaxed text-gray max-xl:pr-5
                                                                                                    max-xl:after:content-empty max-xl:after:!block max-xl:after:absolute max-xl:after:arrow-grey-light max-xl:after:top-2.25 max-xl:after:right-0"
                                                                                                    data-level-two-parent-dropdown={
                                                                                                        l2Id
                                                                                                    }
                                                                                                    // eslint-disable-next-line react/no-danger
                                                                                                    dangerouslySetInnerHTML={{
                                                                                                        __html: renderSanitizedHtmlContent(
                                                                                                            l2Title
                                                                                                        ),
                                                                                                    }}
                                                                                                />
                                                                                            )}
                                                                                            {l2Description && (
                                                                                                <span
                                                                                                    className="cursor-pointer mt-2.5 text-xs text-gray leading-snug xl:text-sm"
                                                                                                    data-level-two-parent-dropdown={
                                                                                                        l2Id
                                                                                                    }
                                                                                                    // eslint-disable-next-line react/no-danger
                                                                                                    dangerouslySetInnerHTML={{
                                                                                                        __html: renderSanitizedHtmlContent(
                                                                                                            l2Description
                                                                                                        ),
                                                                                                    }}
                                                                                                />
                                                                                            )}
                                                                                        </Link>
                                                                                        {// TODO: consider refactoring this block to LevelTwoMenuMobile and moving
                                                                                        // to separate file
                                                                                        levelTwoMenuItem?.subItems && (
                                                                                            <div
                                                                                                data-level-two-child-dropdown={
                                                                                                    l2Id
                                                                                                }
                                                                                                className={`mobileSubmenu mobileSubmenuLevelThree ${styles.mobileSubmenu}`}
                                                                                            >
                                                                                                <div
                                                                                                    role="button"
                                                                                                    onClick={
                                                                                                        subMenuOnClick
                                                                                                    }
                                                                                                    tabIndex={-1}
                                                                                                    onKeyDown={(e) => {
                                                                                                        handleKeyboardEventBehavior(
                                                                                                            e,
                                                                                                            () => {
                                                                                                                subMenuOnClick(
                                                                                                                    e
                                                                                                                );
                                                                                                            },
                                                                                                            ["Escape"]
                                                                                                        );
                                                                                                    }}
                                                                                                    data-child-dropdown-id={
                                                                                                        l2Id
                                                                                                    }
                                                                                                    className="cursor-pointer flex items-center gap-3.75 px-5 py-3.75 bg-stone-light text-sm font-medium"
                                                                                                >
                                                                                                    <span className="arrow-grey rotate-180"></span>
                                                                                                    Back
                                                                                                </div>
                                                                                                <div className="overflow-x-auto pt-7.5 pb-15 px-5 h-[calc(100%-60px)]">
                                                                                                    {/* 3nd level Submenu */}
                                                                                                    <LevelThreeMenuMobile
                                                                                                        data={
                                                                                                            levelTwoMenuItem
                                                                                                        }
                                                                                                        layoutType={
                                                                                                            l2ItemName
                                                                                                        }
                                                                                                        layoutId={
                                                                                                            l2Index
                                                                                                        }
                                                                                                    />
                                                                                                </div>
                                                                                            </div>
                                                                                        )}
                                                                                    </li>
                                                                                );
                                                                            }
                                                                        )}
                                                                    </ul>
                                                                </div>
                                                            )}
                                                        </li>
                                                    )
                                            )}
                                        </ul>
                                    </div>
                                    <div
                                        className={`${styles.headerNavigationBottomRight} headerNavigationBottomRight max-xl:order-3 xl:self-center xl:justify-self-end`}
                                    >
                                        <ul className="flex gap-2.5 max-xl:py-10.25 max-xl:flex-wrap max-xl:order-3 max-xl:pt-14.25">
                                            <li className="menuItem menuItemSearch flex items-center">
                                                <Search
                                                    className="ml-auto mr-5 w-7.5 h-7.5 hidden xl:block"
                                                    searchPopupData={data?.searchPopupData}
                                                />
                                            </li>
                                            {ctaButtons?.map((button) => (
                                                <li key={button?.id}>
                                                    <Cta
                                                        key={button.id}
                                                        data={button}
                                                        className="py-3 px-4.5 text-sm max-xl:px-7.25 max-xl:py-3.25"
                                                    />
                                                </li>
                                            ))}
                                            {!isMobile && (
                                                <li className="menuItem menuItemLangSelector flex items-center">
                                                    <LanguageSelector />
                                                </li>
                                            )}
                                        </ul>
                                    </div>
                                </div>
                                <Search
                                    className="ml-auto mr-7.5 w-7.5 h-7.5 xl:hidden"
                                    searchPopupData={data?.searchPopupData}
                                />
                                <button
                                    className="menuToggle max-xl:w-7.5  max-xl:h-7.5  max-xl:bg-hamburger  max-xl:bg-no-repeat max-xl:bg-center xl:hidden"
                                    onClick={toggleMenu}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Dropdowns mainMenu={mainMenu} activeLink={activeLink} setActiveLink={setActiveLink} />
        </header>
    );
};

export default Header;
